import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";

export interface Product {
  id: number;
  name: string;
  size: "A5" | "A4" | "Custom";
  unitPrice: number;
  status: "active" | "archived";
  /** Polaroid size for print-canvas products; null for other products. */
  printFormat?: "mini" | "medium" | "large" | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductDTO {
  name: string;
  size: "A5" | "A4" | "Custom";
  unitPrice: number;
  status?: "active" | "archived";
  printFormat?: "mini" | "medium" | "large" | null;
}

export interface UpdateProductDTO {
  id: number;
  data: Partial<CreateProductDTO>;
}

export function useProducts() {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      return apiClient.get<Product[]>("/products");
    },
  });
}

export function useActiveProducts() {
  return useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => {
      return apiClient.get<Product[]>("/products/active");
    },
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateProductDTO) => {
      return apiClient.post<Product>("/products", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateProductDTO) => {
      return apiClient.put<Product>(`/products/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
